/**
 * RC-WEB-6 — strings for the reconciled customer journey (Protections page and
 * the ONE canonical checkout). Merged into the `app.*` namespace, so the four
 * locales must stay key-for-key identical.
 */

const es = {
  acc: {
    load_error: "No pudimos cargar tu perfil",
    saved: "Datos guardados",
    save_error: "No se pudo guardar",
    pwd_mismatch: "Las contraseñas no coinciden",
    pwd_short: "La contraseña debe tener al menos 6 caracteres",
    pwd_updated: "Contraseña actualizada",
    pwd_error: "No se pudo cambiar la contraseña",
    title: "Datos de la cuenta",
    desc: "Tu nombre, correo y país. Así te identificamos y te damos soporte.",
    full_name: "Nombre completo",
    email: "Correo electrónico",
    country: "País",
    country_hint: "Define la estimación local del precio y el idioma por defecto si no eliges uno.",
    phone: "Teléfono (opcional)",
    phone_note: "Solo para soporte y avisos de tu cuenta. Tu número de teléfono no se muestra a quien escanea tu QR, no viaja en el QR y no se usa para enrutar las llamadas.",
    save: "Guardar cambios",
    saving: "Guardando…",
    pwd_title: "Contraseña",
    pwd_desc: "Cámbiala cuando quieras. Al cambiarla se cierran las demás sesiones.",
    pwd_current: "Contraseña actual",
    pwd_new: "Nueva contraseña",
    pwd_confirm: "Repetir nueva contraseña",
    pwd_btn: "Cambiar contraseña",
  },
  notif: {
    load_error: "No pudimos cargar tus preferencias",
    save_error: "No se pudo guardar",
    title: "Qué avisos quieres recibir",
    desc: "Elige el tipo de aviso. AvisoCar usa el canal disponible en tu dispositivo; tú no tienes que configurar nada técnico.",
    saving: "Guardando…",
    msg_label: "Mensajes",
    msg_desc: "Cuando alguien que escanea tu QR te escribe.",
    call_label: "Llamadas",
    call_desc: "Cuando alguien pide hablar contigo por AvisoCar.",
    repeat_label: "Repetir aviso urgente si aún no lo viste",
    repeat_desc: "Solo cuando hay un aviso urgente real y no lo has abierto.",
    app_title: "Avisos en la app AvisoCar",
    app_desc: "La app es la forma más fiable de recibir mensajes y llamadas, también con la pantalla bloqueada.",
    app_pending: "Cuando la app esté publicada verás aquí el enlace de descarga. Mientras tanto, tus avisos llegan por correo y, si lo activas, por notificaciones del navegador.",
    devices: "Dispositivos con la app registrados",
    browser_title: "Notificaciones en este navegador (opcional)",
    browser_alt: "Alternativa mientras no uses la app.",
    on: "Activadas",
    off: "Desactivadas",
    enable_btn: "Activar en este navegador",
    enabled_toast: "Notificaciones del navegador activadas",
    disable_btn: "Desactivar aquí",
    disabled_toast: "Desactivadas en este navegador",
    test_btn: "Enviar aviso de prueba",
    test_ok: "Aviso de prueba enviado",
    test_fail: "El aviso de prueba no se pudo entregar",
    unsupported: "Este navegador no admite notificaciones. Puedes seguir viendo tus mensajes al abrir AvisoCar, o consultar la",
    unsupported_link: "guía de uso en el navegador",
    best_effort: "Los avisos se entregan por mejor esfuerzo sobre las notificaciones de tu dispositivo y no sustituyen a los servicios de emergencia.",
  },
  set: {
    del_title: "Eliminar mi cuenta",
    del_body: "Tendrás {n} días para cambiar de opinión. Después, tus datos personales se eliminan y tus QR impresos quedan retirados de forma permanente.",
    del_open_btn: "Quiero eliminar mi cuenta",
    del_policy_link: "Ver la política pública de eliminación",
    del_scheduled: "Tu cuenta se eliminará el",
    del_scheduled_note: "Mientras tanto tus QR no generan avisos nuevos.",
    del_cancel_btn: "Cancelar eliminación",
    del_password: "Confirma tu contraseña",
    del_reason: "Motivo (opcional)",
    del_understand: "Entiendo que esta acción elimina mi cuenta y retira mis QR de forma permanente.",
    del_submit: "Programar eliminación",
    del_abort: "Cancelar",
    del_requested: "Solicitud registrada",
    del_bad_password: "Contraseña incorrecta",
    del_request_error: "No pudimos registrar la solicitud",
    del_cancelled: "Eliminación cancelada",
    del_cancel_error: "No pudimos cancelar la eliminación",
    nav_account: "Cuenta",
    nav_notifications: "Notificaciones",
    nav_trusted: "Contactos de confianza",
    nav_membership: "Membresía y pagos",
    nav_privacy: "Privacidad y seguridad",
    nav_blocks: "Conversaciones bloqueadas",
    nav_language: "Idioma",
    nav_help: "Ayuda",
    nav_delete: "Eliminar cuenta",
    trusted_title: "Contactos de confianza",
    trusted_desc: "Elige quién más recibe los mensajes y las llamadas de tus QR. Nadie comparte su número de teléfono.",
    trusted_btn: "Gestionar contactos",
    membership_title: "Membresía y pagos",
    membership_desc: "Revisa el estado de tu membresía, tu historial de pagos y renueva cuando lo necesites.",
    membership_btn: "Ver membresía y pagos",
    privacy_title: "Privacidad y seguridad",
    privacy_desc: "Cómo trata AvisoCar tus datos y qué controlas tú.",
    privacy_b1: "Tu número de teléfono no se muestra a quien escanea tu QR.",
    privacy_b2: "Las llamadas y los mensajes pasan por AvisoCar: nadie ve el contacto del otro.",
    privacy_b3: "Puedes pausar los avisos de un QR o rotar el código cuando quieras.",
    privacy_b4: "Rotar el QR solo invalida el código impreso: tu historial y tus contactos siguen igual.",
    privacy_doc: "Política de privacidad",
    privacy_terms: "Términos del servicio",
    lang_title: "Idioma de la cuenta",
    lang_desc: "Se usa en la app y en los avisos y correos que te enviamos.",
    lang_auto: "Automático (según tu país)",
    lang_effective: "Idioma efectivo",
    lang_saved: "Idioma actualizado",
    lang_error: "No pudimos guardar el idioma",
  },
  help: {
    title: "Ayuda",
    desc: "Guías rápidas y contacto con el equipo de AvisoCar.",
    howto: "Cómo usar AvisoCar",
    contact: "Contactar soporte",
    store_pending_title: "La app de AvisoCar todavía no está publicada",
    store_pending_text: "Hoy usas AvisoCar desde el navegador, con todas sus funciones. Cuando la app esté en Google Play o App Store, verás aquí el botón de descarga.",
    android_download: "Descargar para Android",
    ios_download: "Descargar para iPhone",
    browser_secondary: "Cómo añadir AvisoCar a tu pantalla de inicio (opcional)",
  },
  protections: {
    title: "Protecciones",
    subtitle: "Todo lo que proteges con AvisoCar, en un solo lugar",
    new: "Nueva protección",
    empty_title: "Aún no proteges nada",
    empty_text: "Crea tu primera protección: elige qué quieres proteger, activa su QR y recibe avisos privados de quien lo escanee.",
    status_active: "QR activo",
    status_review: "Pago en revisión",
    status_pending: "Pendiente de activación",
    status_paused: "QR en pausa",
    view: "Ver detalle",
    sticker: "Sticker Studio",
    activate: "Activar protección",
    count_one: "1 protección",
    count_many: "{n} protecciones",
    active_of: "{active} con QR activo de {total}",
    plate: "Placa",
    type_vehicle: "Vehículo",
    type_kid: "Niño o niña",
    type_pet: "Mascota",
    type_object: "Objeto",
    type_other: "Otro",
  },
  checkout: {
    title_renew: "Membresía y pagos",
    subtitle_renew: "Tu protección, tu plan y cómo pagarlo",
    title_activate: "Activar protección",
    subtitle_activate: "Elige tu plan y envía tu pago para encender este QR",
    hero_activate_title: "Falta activar: {name}",
    hero_activate_text: "Cuando confirmemos tu pago, el QR de esta protección queda activo y podrás recibir mensajes y llamadas privadas.",
    what_you_pay: "Qué estás pagando",
    ctx_activate: "Activar protección · {name}",
    ctx_renew: "Renovar la membresía de tu cuenta",
    entitlement_note: "La membresía es de tu cuenta y cubre la duración del plan. Cada protección se enciende cuando su pago queda aprobado.",
    qr_note: "El sticker impreso no caduca, pero el servicio (mensajes, llamadas y avisos) funciona mientras tu membresía esté vigente.",
    voucher_title: "Envía tu comprobante",
    voucher_text: "Sube la captura o el PDF de tu pago. Lo revisamos y activamos esta protección.",
    voucher_select: "Elegir comprobante",
    voucher_change: "Cambiar comprobante",
    voucher_limit: "JPG, PNG o PDF · hasta 8 MB",
    voucher_type_error: "Formato no válido. Usa JPG, PNG o PDF.",
    voucher_size_error: "El archivo supera los 8 MB.",
    voucher_submit: "Enviar comprobante",
    voucher_submitting: "Enviando…",
    voucher_sent: "Comprobante enviado. Lo revisamos y te avisamos dentro de AvisoCar.",
    voucher_selected: "Comprobante listo para enviar",
    review_title: "Tu pago está en revisión",
    review_text: "Ya recibimos un comprobante para esta protección. Te avisamos dentro de AvisoCar en cuanto quede activa.",
    rejected_title: "No pudimos validar tu pago",
    wa_note: "Si lo envías por WhatsApp, nuestro equipo debe revisarlo manualmente: no queda aprobado de forma automática.",
    ref_label: "Tu referencia de pago",
    back_to_protection: "Volver a la protección",
    already_active: "Esta protección ya está activa.",
    not_found: "No encontramos esta protección.",
  },
  sticker: {
    title: "Sticker Studio",
    subtitle: "Personaliza cómo las personas podrán avisarte cuando escaneen tu QR.",
    open: "Abrir Studio",
    download: "Descargar",
    draft_saved: "Diseño guardado",
    no_draft: "Sin diseño guardado",
    pending_label: "Pendiente de activación",
    pending_text: "Puedes preparar el diseño, pero el QR no funcionará hasta que activemos esta protección.",
    activate: "Activar protección",
    empty_title: "Crea tu primera protección",
    empty_text: "El Sticker Studio se abre desde cada protección: elige plantilla, colores y textos, y descarga el sticker con tu QR.",
  },
};

const en = {
  acc: {
    load_error: "We couldn't load your profile",
    saved: "Details saved",
    save_error: "We couldn't save",
    pwd_mismatch: "The passwords don't match",
    pwd_short: "The password must be at least 6 characters",
    pwd_updated: "Password updated",
    pwd_error: "We couldn't change the password",
    title: "Account details",
    desc: "Your name, email and country. That's how we identify you and give you support.",
    full_name: "Full name",
    email: "Email",
    country: "Country",
    country_hint: "Sets the local price estimate and the default language if you don't choose one.",
    phone: "Phone (optional)",
    phone_note: "For support and account alerts only. Your phone number is never shown to whoever scans your QR, never travels in the QR and is never used to route calls.",
    save: "Save changes",
    saving: "Saving…",
    pwd_title: "Password",
    pwd_desc: "Change it whenever you want. Changing it signs out your other sessions.",
    pwd_current: "Current password",
    pwd_new: "New password",
    pwd_confirm: "Repeat new password",
    pwd_btn: "Change password",
  },
  notif: {
    load_error: "We couldn't load your preferences",
    save_error: "We couldn't save",
    title: "Which alerts you want to receive",
    desc: "Choose the kind of alert. AvisoCar uses whatever channel your device supports; you don't have to configure anything technical.",
    saving: "Saving…",
    msg_label: "Messages",
    msg_desc: "When someone who scans your QR writes to you.",
    call_label: "Calls",
    call_desc: "When someone asks to talk to you through AvisoCar.",
    repeat_label: "Repeat an urgent alert if you haven't seen it",
    repeat_desc: "Only when there is a real urgent alert and you haven't opened it.",
    app_title: "Alerts in the AvisoCar app",
    app_desc: "The app is the most reliable way to get messages and calls, even with the screen locked.",
    app_pending: "When the app is published you'll see the download link here. Meanwhile your alerts arrive by email and, if you enable it, by browser notifications.",
    devices: "Registered devices with the app",
    browser_title: "Notifications in this browser (optional)",
    browser_alt: "An alternative while you don't use the app.",
    on: "On",
    off: "Off",
    enable_btn: "Enable in this browser",
    enabled_toast: "Browser notifications enabled",
    disable_btn: "Disable here",
    disabled_toast: "Disabled in this browser",
    test_btn: "Send a test alert",
    test_ok: "Test alert sent",
    test_fail: "The test alert couldn't be delivered",
    unsupported: "This browser doesn't support notifications. You can still read your messages by opening AvisoCar, or check the",
    unsupported_link: "browser usage guide",
    best_effort: "Alerts are delivered on a best-effort basis over your device notifications and do not replace emergency services.",
  },
  set: {
    del_title: "Delete my account",
    del_body: "You'll have {n} days to change your mind. After that, your personal data is deleted and your printed QR codes are permanently withdrawn.",
    del_open_btn: "I want to delete my account",
    del_policy_link: "See the public deletion policy",
    del_scheduled: "Your account will be deleted on",
    del_scheduled_note: "In the meantime your QR codes don't generate new alerts.",
    del_cancel_btn: "Cancel deletion",
    del_password: "Confirm your password",
    del_reason: "Reason (optional)",
    del_understand: "I understand this deletes my account and permanently withdraws my QR codes.",
    del_submit: "Schedule deletion",
    del_abort: "Cancel",
    del_requested: "Request registered",
    del_bad_password: "Incorrect password",
    del_request_error: "We couldn't register the request",
    del_cancelled: "Deletion cancelled",
    del_cancel_error: "We couldn't cancel the deletion",
    nav_account: "Account",
    nav_notifications: "Notifications",
    nav_trusted: "Trusted contacts",
    nav_membership: "Membership and payments",
    nav_privacy: "Privacy and security",
    nav_blocks: "Blocked conversations",
    nav_language: "Language",
    nav_help: "Help",
    nav_delete: "Delete account",
    trusted_title: "Trusted contacts",
    trusted_desc: "Choose who else receives the messages and calls from your QR codes. Nobody shares their phone number.",
    trusted_btn: "Manage contacts",
    membership_title: "Membership and payments",
    membership_desc: "Check your membership status and payment history, and renew whenever you need to.",
    membership_btn: "View membership and payments",
    privacy_title: "Privacy and security",
    privacy_desc: "How AvisoCar handles your data and what you control.",
    privacy_b1: "Your phone number is never shown to whoever scans your QR.",
    privacy_b2: "Calls and messages go through AvisoCar: neither side sees the other's contact details.",
    privacy_b3: "You can pause a QR's alerts or rotate the code whenever you want.",
    privacy_b4: "Rotating the QR only invalidates the printed code: your history and contacts stay the same.",
    privacy_doc: "Privacy policy",
    privacy_terms: "Terms of service",
    lang_title: "Account language",
    lang_desc: "Used in the app and in the alerts and emails we send you.",
    lang_auto: "Automatic (based on your country)",
    lang_effective: "Effective language",
    lang_saved: "Language updated",
    lang_error: "We couldn't save the language",
  },
  help: {
    title: "Help",
    desc: "Quick guides and contact with the AvisoCar team.",
    howto: "How to use AvisoCar",
    contact: "Contact support",
    store_pending_title: "The AvisoCar app isn't published yet",
    store_pending_text: "Today you use AvisoCar in your browser, with all its features. When the app is on Google Play or the App Store, the download button will appear here.",
    android_download: "Download for Android",
    ios_download: "Download for iPhone",
    browser_secondary: "How to add AvisoCar to your home screen (optional)",
  },
  protections: {
    title: "Protections",
    subtitle: "Everything you protect with AvisoCar, in one place",
    new: "New protection",
    empty_title: "You're not protecting anything yet",
    empty_text: "Create your first protection: choose what to protect, activate its QR and get private alerts from whoever scans it.",
    status_active: "QR active",
    status_review: "Payment under review",
    status_pending: "Pending activation",
    status_paused: "QR paused",
    view: "View details",
    sticker: "Sticker Studio",
    activate: "Activate protection",
    count_one: "1 protection",
    count_many: "{n} protections",
    active_of: "{active} with an active QR out of {total}",
    plate: "Plate",
    type_vehicle: "Vehicle",
    type_kid: "Child",
    type_pet: "Pet",
    type_object: "Object",
    type_other: "Other",
  },
  checkout: {
    title_renew: "Membership and payments",
    subtitle_renew: "Your protection, your plan and how to pay for it",
    title_activate: "Activate protection",
    subtitle_activate: "Choose your plan and send your payment to turn this QR on",
    hero_activate_title: "Still to activate: {name}",
    hero_activate_text: "Once we confirm your payment, this protection's QR goes live and you can receive private messages and calls.",
    what_you_pay: "What you're paying for",
    ctx_activate: "Activate protection · {name}",
    ctx_renew: "Renew your account membership",
    entitlement_note: "The membership belongs to your account and lasts for the plan duration. Each protection turns on when its payment is approved.",
    qr_note: "The printed sticker doesn't expire, but the service (messages, calls and alerts) works while your membership is valid.",
    voucher_title: "Send your receipt",
    voucher_text: "Upload the screenshot or PDF of your payment. We review it and activate this protection.",
    voucher_select: "Choose receipt",
    voucher_change: "Change receipt",
    voucher_limit: "JPG, PNG or PDF · up to 8 MB",
    voucher_type_error: "Invalid format. Use JPG, PNG or PDF.",
    voucher_size_error: "The file is over 8 MB.",
    voucher_submit: "Send receipt",
    voucher_submitting: "Sending…",
    voucher_sent: "Receipt sent. We'll review it and let you know inside AvisoCar.",
    voucher_selected: "Receipt ready to send",
    review_title: "Your payment is under review",
    review_text: "We already received a receipt for this protection. We'll tell you inside AvisoCar as soon as it's active.",
    rejected_title: "We couldn't validate your payment",
    wa_note: "If you send it over WhatsApp, our team still has to review it manually: it is not approved automatically.",
    ref_label: "Your payment reference",
    back_to_protection: "Back to the protection",
    already_active: "This protection is already active.",
    not_found: "We couldn't find this protection.",
  },
  sticker: {
    title: "Sticker Studio",
    subtitle: "Customize how people can reach you when they scan your QR.",
    open: "Open Studio",
    download: "Download",
    draft_saved: "Design saved",
    no_draft: "No saved design",
    pending_label: "Pending activation",
    pending_text: "You can prepare the design, but the QR won't work until we activate this protection.",
    activate: "Activate protection",
    empty_title: "Create your first protection",
    empty_text: "The Sticker Studio opens from each protection: choose template, colors and texts, and download the sticker with your QR.",
  },
};

const pt = {
  acc: {
    load_error: "Não conseguimos carregar seu perfil",
    saved: "Dados salvos",
    save_error: "Não foi possível salvar",
    pwd_mismatch: "As senhas não coincidem",
    pwd_short: "A senha deve ter pelo menos 6 caracteres",
    pwd_updated: "Senha atualizada",
    pwd_error: "Não foi possível alterar a senha",
    title: "Dados da conta",
    desc: "Seu nome, e-mail e país. É assim que identificamos você e damos suporte.",
    full_name: "Nome completo",
    email: "E-mail",
    country: "País",
    country_hint: "Define a estimativa local de preço e o idioma padrão se você não escolher um.",
    phone: "Telefone (opcional)",
    phone_note: "Apenas para suporte e avisos da conta. Seu telefone nunca aparece para quem escaneia seu QR, não vai no QR e não é usado para encaminhar chamadas.",
    save: "Salvar alterações",
    saving: "Salvando…",
    pwd_title: "Senha",
    pwd_desc: "Troque quando quiser. Ao trocar, as outras sessões são encerradas.",
    pwd_current: "Senha atual",
    pwd_new: "Nova senha",
    pwd_confirm: "Repetir a nova senha",
    pwd_btn: "Alterar senha",
  },
  notif: {
    load_error: "Não conseguimos carregar suas preferências",
    save_error: "Não foi possível salvar",
    title: "Quais avisos você quer receber",
    desc: "Escolha o tipo de aviso. O AvisoCar usa o canal disponível no seu aparelho; você não precisa configurar nada técnico.",
    saving: "Salvando…",
    msg_label: "Mensagens",
    msg_desc: "Quando alguém que escaneia seu QR escreve para você.",
    call_label: "Chamadas",
    call_desc: "Quando alguém pede para falar com você pelo AvisoCar.",
    repeat_label: "Repetir aviso urgente se você ainda não viu",
    repeat_desc: "Só quando há um aviso urgente real e você não abriu.",
    app_title: "Avisos no app AvisoCar",
    app_desc: "O app é a forma mais confiável de receber mensagens e chamadas, mesmo com a tela bloqueada.",
    app_pending: "Quando o app for publicado, o link de download aparecerá aqui. Enquanto isso, seus avisos chegam por e-mail e, se você ativar, por notificações do navegador.",
    devices: "Aparelhos com o app registrados",
    browser_title: "Notificações neste navegador (opcional)",
    browser_alt: "Alternativa enquanto você não usa o app.",
    on: "Ativadas",
    off: "Desativadas",
    enable_btn: "Ativar neste navegador",
    enabled_toast: "Notificações do navegador ativadas",
    disable_btn: "Desativar aqui",
    disabled_toast: "Desativadas neste navegador",
    test_btn: "Enviar aviso de teste",
    test_ok: "Aviso de teste enviado",
    test_fail: "O aviso de teste não pôde ser entregue",
    unsupported: "Este navegador não suporta notificações. Você ainda pode ver suas mensagens abrindo o AvisoCar, ou consultar o",
    unsupported_link: "guia de uso no navegador",
    best_effort: "Os avisos são entregues por melhor esforço sobre as notificações do aparelho e não substituem os serviços de emergência.",
  },
  set: {
    del_title: "Excluir minha conta",
    del_body: "Você terá {n} dias para mudar de ideia. Depois, seus dados pessoais são excluídos e seus QR impressos ficam retirados permanentemente.",
    del_open_btn: "Quero excluir minha conta",
    del_policy_link: "Ver a política pública de exclusão",
    del_scheduled: "Sua conta será excluída em",
    del_scheduled_note: "Enquanto isso, seus QR não geram novos avisos.",
    del_cancel_btn: "Cancelar exclusão",
    del_password: "Confirme sua senha",
    del_reason: "Motivo (opcional)",
    del_understand: "Entendo que isso exclui minha conta e retira meus QR de forma permanente.",
    del_submit: "Programar exclusão",
    del_abort: "Cancelar",
    del_requested: "Solicitação registrada",
    del_bad_password: "Senha incorreta",
    del_request_error: "Não conseguimos registrar a solicitação",
    del_cancelled: "Exclusão cancelada",
    del_cancel_error: "Não conseguimos cancelar a exclusão",
    nav_account: "Conta",
    nav_notifications: "Notificações",
    nav_trusted: "Contatos de confiança",
    nav_membership: "Assinatura e pagamentos",
    nav_privacy: "Privacidade e segurança",
    nav_blocks: "Conversas bloqueadas",
    nav_language: "Idioma",
    nav_help: "Ajuda",
    nav_delete: "Excluir conta",
    trusted_title: "Contatos de confiança",
    trusted_desc: "Escolha quem mais recebe as mensagens e chamadas dos seus QR. Ninguém compartilha o número de telefone.",
    trusted_btn: "Gerenciar contatos",
    membership_title: "Assinatura e pagamentos",
    membership_desc: "Veja o status da sua assinatura, o histórico de pagamentos e renove quando precisar.",
    membership_btn: "Ver assinatura e pagamentos",
    privacy_title: "Privacidade e segurança",
    privacy_desc: "Como o AvisoCar trata seus dados e o que você controla.",
    privacy_b1: "Seu número de telefone nunca aparece para quem escaneia seu QR.",
    privacy_b2: "Chamadas e mensagens passam pelo AvisoCar: ninguém vê o contato do outro.",
    privacy_b3: "Você pode pausar os avisos de um QR ou trocar o código quando quiser.",
    privacy_b4: "Trocar o QR só invalida o código impresso: seu histórico e seus contatos continuam iguais.",
    privacy_doc: "Política de privacidade",
    privacy_terms: "Termos do serviço",
    lang_title: "Idioma da conta",
    lang_desc: "Usado no app e nos avisos e e-mails que enviamos para você.",
    lang_auto: "Automático (conforme seu país)",
    lang_effective: "Idioma efetivo",
    lang_saved: "Idioma atualizado",
    lang_error: "Não conseguimos salvar o idioma",
  },
  help: {
    title: "Ajuda",
    desc: "Guias rápidos e contato com a equipe do AvisoCar.",
    howto: "Como usar o AvisoCar",
    contact: "Falar com o suporte",
    store_pending_title: "O app do AvisoCar ainda não foi publicado",
    store_pending_text: "Hoje você usa o AvisoCar pelo navegador, com todas as funções. Quando o app estiver no Google Play ou na App Store, o botão de download aparecerá aqui.",
    android_download: "Baixar para Android",
    ios_download: "Baixar para iPhone",
    browser_secondary: "Como adicionar o AvisoCar à tela inicial (opcional)",
  },
  protections: {
    title: "Proteções",
    subtitle: "Tudo o que você protege com o AvisoCar, em um só lugar",
    new: "Nova proteção",
    empty_title: "Você ainda não protege nada",
    empty_text: "Crie sua primeira proteção: escolha o que proteger, ative o QR e receba avisos privados de quem escanear.",
    status_active: "QR ativo",
    status_review: "Pagamento em análise",
    status_pending: "Pendente de ativação",
    status_paused: "QR pausado",
    view: "Ver detalhes",
    sticker: "Sticker Studio",
    activate: "Ativar proteção",
    count_one: "1 proteção",
    count_many: "{n} proteções",
    active_of: "{active} com QR ativo de {total}",
    plate: "Placa",
    type_vehicle: "Veículo",
    type_kid: "Criança",
    type_pet: "Animal",
    type_object: "Objeto",
    type_other: "Outro",
  },
  checkout: {
    title_renew: "Assinatura e pagamentos",
    subtitle_renew: "Sua proteção, seu plano e como pagar",
    title_activate: "Ativar proteção",
    subtitle_activate: "Escolha seu plano e envie o pagamento para ligar este QR",
    hero_activate_title: "Falta ativar: {name}",
    hero_activate_text: "Quando confirmarmos seu pagamento, o QR desta proteção fica ativo e você poderá receber mensagens e chamadas privadas.",
    what_you_pay: "O que você está pagando",
    ctx_activate: "Ativar proteção · {name}",
    ctx_renew: "Renovar a assinatura da sua conta",
    entitlement_note: "A assinatura é da sua conta e vale pela duração do plano. Cada proteção é ligada quando o pagamento dela é aprovado.",
    qr_note: "O adesivo impresso não vence, mas o serviço (mensagens, chamadas e avisos) funciona enquanto sua assinatura estiver válida.",
    voucher_title: "Envie seu comprovante",
    voucher_text: "Envie a captura ou o PDF do pagamento. Analisamos e ativamos esta proteção.",
    voucher_select: "Escolher comprovante",
    voucher_change: "Trocar comprovante",
    voucher_limit: "JPG, PNG ou PDF · até 8 MB",
    voucher_type_error: "Formato inválido. Use JPG, PNG ou PDF.",
    voucher_size_error: "O arquivo passa de 8 MB.",
    voucher_submit: "Enviar comprovante",
    voucher_submitting: "Enviando…",
    voucher_sent: "Comprovante enviado. Vamos analisar e avisar dentro do AvisoCar.",
    voucher_selected: "Comprovante pronto para enviar",
    review_title: "Seu pagamento está em análise",
    review_text: "Já recebemos um comprovante para esta proteção. Avisamos dentro do AvisoCar assim que ficar ativa.",
    rejected_title: "Não conseguimos validar seu pagamento",
    wa_note: "Se enviar pelo WhatsApp, nossa equipe ainda precisa analisar manualmente: não é aprovado automaticamente.",
    ref_label: "Sua referência de pagamento",
    back_to_protection: "Voltar para a proteção",
    already_active: "Esta proteção já está ativa.",
    not_found: "Não encontramos esta proteção.",
  },
  sticker: {
    title: "Sticker Studio",
    subtitle: "Personalize como as pessoas poderão avisar você ao escanear seu QR.",
    open: "Abrir Studio",
    download: "Baixar",
    draft_saved: "Design salvo",
    no_draft: "Sem design salvo",
    pending_label: "Pendente de ativação",
    pending_text: "Você pode preparar o design, mas o QR não vai funcionar até ativarmos esta proteção.",
    activate: "Ativar proteção",
    empty_title: "Crie sua primeira proteção",
    empty_text: "O Sticker Studio abre a partir de cada proteção: escolha modelo, cores e textos e baixe o adesivo com seu QR.",
  },
};

const fr = {
  acc: {
    load_error: "Nous n'avons pas pu charger votre profil",
    saved: "Informations enregistrées",
    save_error: "Enregistrement impossible",
    pwd_mismatch: "Les mots de passe ne correspondent pas",
    pwd_short: "Le mot de passe doit contenir au moins 6 caractères",
    pwd_updated: "Mot de passe mis à jour",
    pwd_error: "Impossible de changer le mot de passe",
    title: "Informations du compte",
    desc: "Vos nom, e-mail et pays. C'est ainsi que nous vous identifions et vous assistons.",
    full_name: "Nom complet",
    email: "E-mail",
    country: "Pays",
    country_hint: "Définit l'estimation locale du prix et la langue par défaut si vous n'en choisissez pas.",
    phone: "Téléphone (facultatif)",
    phone_note: "Uniquement pour le support et les alertes du compte. Votre numéro n'est jamais montré à qui scanne votre QR, ne voyage pas dans le QR et ne sert pas à router les appels.",
    save: "Enregistrer",
    saving: "Enregistrement…",
    pwd_title: "Mot de passe",
    pwd_desc: "Changez-le quand vous voulez. Cela déconnecte vos autres sessions.",
    pwd_current: "Mot de passe actuel",
    pwd_new: "Nouveau mot de passe",
    pwd_confirm: "Répéter le nouveau mot de passe",
    pwd_btn: "Changer le mot de passe",
  },
  notif: {
    load_error: "Nous n'avons pas pu charger vos préférences",
    save_error: "Enregistrement impossible",
    title: "Quelles alertes voulez-vous recevoir",
    desc: "Choisissez le type d'alerte. AvisoCar utilise le canal disponible sur votre appareil ; rien de technique à configurer.",
    saving: "Enregistrement…",
    msg_label: "Messages",
    msg_desc: "Quand quelqu'un qui scanne votre QR vous écrit.",
    call_label: "Appels",
    call_desc: "Quand quelqu'un demande à vous parler via AvisoCar.",
    repeat_label: "Répéter une alerte urgente si vous ne l'avez pas vue",
    repeat_desc: "Uniquement en cas d'alerte urgente réelle non ouverte.",
    app_title: "Alertes dans l'application AvisoCar",
    app_desc: "L'application est le moyen le plus fiable de recevoir messages et appels, même écran verrouillé.",
    app_pending: "Quand l'application sera publiée, le lien de téléchargement apparaîtra ici. En attendant, vos alertes arrivent par e-mail et, si vous l'activez, par notifications du navigateur.",
    devices: "Appareils enregistrés avec l'application",
    browser_title: "Notifications dans ce navigateur (facultatif)",
    browser_alt: "Une alternative en attendant l'application.",
    on: "Activées",
    off: "Désactivées",
    enable_btn: "Activer dans ce navigateur",
    enabled_toast: "Notifications du navigateur activées",
    disable_btn: "Désactiver ici",
    disabled_toast: "Désactivées dans ce navigateur",
    test_btn: "Envoyer une alerte test",
    test_ok: "Alerte test envoyée",
    test_fail: "L'alerte test n'a pas pu être livrée",
    unsupported: "Ce navigateur ne prend pas en charge les notifications. Vous pouvez toujours lire vos messages en ouvrant AvisoCar, ou consulter le",
    unsupported_link: "guide d'utilisation dans le navigateur",
    best_effort: "Les alertes sont livrées au mieux via les notifications de votre appareil et ne remplacent pas les services d'urgence.",
  },
  set: {
    del_title: "Supprimer mon compte",
    del_body: "Vous aurez {n} jours pour changer d'avis. Ensuite, vos données personnelles sont supprimées et vos QR imprimés sont retirés définitivement.",
    del_open_btn: "Je veux supprimer mon compte",
    del_policy_link: "Voir la politique publique de suppression",
    del_scheduled: "Votre compte sera supprimé le",
    del_scheduled_note: "En attendant, vos QR ne génèrent plus de nouvelles alertes.",
    del_cancel_btn: "Annuler la suppression",
    del_password: "Confirmez votre mot de passe",
    del_reason: "Motif (facultatif)",
    del_understand: "Je comprends que cela supprime mon compte et retire définitivement mes QR.",
    del_submit: "Programmer la suppression",
    del_abort: "Annuler",
    del_requested: "Demande enregistrée",
    del_bad_password: "Mot de passe incorrect",
    del_request_error: "Nous n'avons pas pu enregistrer la demande",
    del_cancelled: "Suppression annulée",
    del_cancel_error: "Nous n'avons pas pu annuler la suppression",
    nav_account: "Compte",
    nav_notifications: "Notifications",
    nav_trusted: "Contacts de confiance",
    nav_membership: "Abonnement et paiements",
    nav_privacy: "Confidentialité et sécurité",
    nav_blocks: "Conversations bloquées",
    nav_language: "Langue",
    nav_help: "Aide",
    nav_delete: "Supprimer le compte",
    trusted_title: "Contacts de confiance",
    trusted_desc: "Choisissez qui reçoit aussi les messages et les appels de vos QR. Personne ne partage son numéro de téléphone.",
    trusted_btn: "Gérer les contacts",
    membership_title: "Abonnement et paiements",
    membership_desc: "Consultez l'état de votre abonnement, votre historique de paiements et renouvelez quand vous voulez.",
    membership_btn: "Voir l'abonnement et les paiements",
    privacy_title: "Confidentialité et sécurité",
    privacy_desc: "Comment AvisoCar traite vos données et ce que vous contrôlez.",
    privacy_b1: "Votre numéro de téléphone n'est jamais montré à qui scanne votre QR.",
    privacy_b2: "Appels et messages passent par AvisoCar : personne ne voit le contact de l'autre.",
    privacy_b3: "Vous pouvez suspendre les alertes d'un QR ou changer le code quand vous voulez.",
    privacy_b4: "Changer le QR invalide seulement le code imprimé : votre historique et vos contacts restent identiques.",
    privacy_doc: "Politique de confidentialité",
    privacy_terms: "Conditions du service",
    lang_title: "Langue du compte",
    lang_desc: "Utilisée dans l'application et dans les alertes et e-mails que nous vous envoyons.",
    lang_auto: "Automatique (selon votre pays)",
    lang_effective: "Langue effective",
    lang_saved: "Langue mise à jour",
    lang_error: "Nous n'avons pas pu enregistrer la langue",
  },
  help: {
    title: "Aide",
    desc: "Guides rapides et contact avec l'équipe AvisoCar.",
    howto: "Comment utiliser AvisoCar",
    contact: "Contacter le support",
    store_pending_title: "L'application AvisoCar n'est pas encore publiée",
    store_pending_text: "Aujourd'hui vous utilisez AvisoCar dans votre navigateur, avec toutes ses fonctions. Quand l'application sera sur Google Play ou l'App Store, le bouton de téléchargement apparaîtra ici.",
    android_download: "Télécharger pour Android",
    ios_download: "Télécharger pour iPhone",
    browser_secondary: "Comment ajouter AvisoCar à votre écran d'accueil (optionnel)",
  },
  protections: {
    title: "Protections",
    subtitle: "Tout ce que vous protégez avec AvisoCar, au même endroit",
    new: "Nouvelle protection",
    empty_title: "Vous ne protégez rien pour l'instant",
    empty_text: "Créez votre première protection : choisissez quoi protéger, activez son QR et recevez des alertes privées de qui le scanne.",
    status_active: "QR actif",
    status_review: "Paiement en vérification",
    status_pending: "En attente d'activation",
    status_paused: "QR en pause",
    view: "Voir le détail",
    sticker: "Sticker Studio",
    activate: "Activer la protection",
    count_one: "1 protection",
    count_many: "{n} protections",
    active_of: "{active} avec QR actif sur {total}",
    plate: "Plaque",
    type_vehicle: "Véhicule",
    type_kid: "Enfant",
    type_pet: "Animal",
    type_object: "Objet",
    type_other: "Autre",
  },
  checkout: {
    title_renew: "Abonnement et paiements",
    subtitle_renew: "Votre protection, votre formule et comment la payer",
    title_activate: "Activer la protection",
    subtitle_activate: "Choisissez votre formule et envoyez votre paiement pour allumer ce QR",
    hero_activate_title: "Reste à activer : {name}",
    hero_activate_text: "Dès que nous confirmons votre paiement, le QR de cette protection devient actif et vous pourrez recevoir messages et appels privés.",
    what_you_pay: "Ce que vous payez",
    ctx_activate: "Activer la protection · {name}",
    ctx_renew: "Renouveler l'abonnement de votre compte",
    entitlement_note: "L'abonnement appartient à votre compte et dure le temps de la formule. Chaque protection s'active quand son paiement est approuvé.",
    qr_note: "L'autocollant imprimé n'expire pas, mais le service (messages, appels et alertes) fonctionne tant que votre abonnement est valide.",
    voucher_title: "Envoyez votre justificatif",
    voucher_text: "Téléversez la capture ou le PDF de votre paiement. Nous le vérifions et activons cette protection.",
    voucher_select: "Choisir un justificatif",
    voucher_change: "Changer de justificatif",
    voucher_limit: "JPG, PNG ou PDF · jusqu'à 8 Mo",
    voucher_type_error: "Format invalide. Utilisez JPG, PNG ou PDF.",
    voucher_size_error: "Le fichier dépasse 8 Mo.",
    voucher_submit: "Envoyer le justificatif",
    voucher_submitting: "Envoi…",
    voucher_sent: "Justificatif envoyé. Nous le vérifions et vous prévenons dans AvisoCar.",
    voucher_selected: "Justificatif prêt à être envoyé",
    review_title: "Votre paiement est en vérification",
    review_text: "Nous avons déjà reçu un justificatif pour cette protection. Nous vous prévenons dans AvisoCar dès qu'elle est active.",
    rejected_title: "Nous n'avons pas pu valider votre paiement",
    wa_note: "Si vous l'envoyez par WhatsApp, notre équipe doit encore le vérifier manuellement : ce n'est pas approuvé automatiquement.",
    ref_label: "Votre référence de paiement",
    back_to_protection: "Retour à la protection",
    already_active: "Cette protection est déjà active.",
    not_found: "Nous n'avons pas trouvé cette protection.",
  },
  sticker: {
    title: "Sticker Studio",
    subtitle: "Personnalisez la façon dont on peut vous prévenir en scannant votre QR.",
    open: "Ouvrir le Studio",
    download: "Télécharger",
    draft_saved: "Design enregistré",
    no_draft: "Aucun design enregistré",
    pending_label: "En attente d'activation",
    pending_text: "Vous pouvez préparer le design, mais le QR ne fonctionnera pas avant l'activation de cette protection.",
    activate: "Activer la protection",
    empty_title: "Créez votre première protection",
    empty_text: "Le Sticker Studio s'ouvre depuis chaque protection : choisissez modèle, couleurs et textes, puis téléchargez l'autocollant avec votre QR.",
  },
};

export const RC_WEB6_LOCALES = { es, en, pt, fr };
export default RC_WEB6_LOCALES;
